import React, { useRef, useEffect, useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import Icon from './Icon';

interface IFilterDropdownProps {
    value: string | null;
    options: Array<{ value: string; label: string }>;
    onChange: (value: string | null) => void;
    placeholder: string;
    disabled?: boolean;
    labelPrefix?: string; // Optional prefix to add to selected value display (e.g., "Category: ")
    renderOption?: (option: { value: string; label: string }) => React.ReactNode; // Custom option renderer (e.g., for icons/colored dots)
    renderSelectedValue?: (option: { value: string; label: string }) => React.ReactNode; // Custom renderer for selected value in toggle
}

const FilterDropdown: React.FC<IFilterDropdownProps> = ({
    value,
    options,
    onChange,
    placeholder,
    disabled = false,
    labelPrefix,
    renderOption,
    renderSelectedValue
}) => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const [positionClass, setPositionClass] = useState('dropdown-left');
    const [isOpen, setIsOpen] = useState(false);
    const lastKeyRef = useRef('');
    const lastKeyIndexRef = useRef(0);

    // Find the selected option
    const selectedOption = options.find(option => option.value === value);
    
    // Build display text with optional prefix for selected values
    let displayText: string;
    let labelPart: string | null = null;
    let valuePart: string;
    
    if (selectedOption && labelPrefix) {
        // Add prefix to selected value (e.g., "Category: AGSS")
        displayText = `${labelPrefix}${selectedOption.label}`;
        labelPart = labelPrefix.replace(':', '').trim();
        valuePart = selectedOption.label;
    } else if (selectedOption) {
        displayText = selectedOption.label;
        valuePart = displayText;
    } else {
        // Use placeholder (e.g., "Category: All")
        displayText = placeholder;
        const parts = displayText.split(': ');
        labelPart = parts.length > 1 && parts[0] ? parts[0] : null;
        valuePart = parts.length > 1 ? parts.slice(1).join(': ') : displayText;
    }

    // Check if current value is a "default" state (no filter applied)
    const isDefaultValue = value === null || value === undefined || value === '' || value === 'All' || value === 'No Options';

    const handleSelect = (selectedValue: any) => {
        if (selectedValue) {
            onChange(selectedValue);
        }
    };

    const handleClear = (e: React.MouseEvent) => {
        e.stopPropagation();
        onChange(null);
    };

    const calculatePosition = () => {
        if (!wrapperRef.current) return;

        const wrapper = wrapperRef.current;
        const wrapperRect = wrapper.getBoundingClientRect();
        const screenWidth = window.innerWidth;
        const dropdownWidth = 250; // Approximate dropdown width

        // Check if dropdown would overflow with default left alignment
        const wouldOverflowRight = wrapperRect.left + dropdownWidth > screenWidth - 20;

        // Check if dropdown would overflow with right alignment
        const wouldOverflowLeft = wrapperRect.right - dropdownWidth < 20;

        if (wouldOverflowRight && wouldOverflowLeft) {
            // Center if both would overflow
            setPositionClass('dropdown-center');
        } else if (wouldOverflowRight) {
            // Right align if left would overflow
            setPositionClass('dropdown-right');
        } else {
            // Default left alignment (no class needed)
            setPositionClass('');
        }
    };

    const handleToggle = (open: boolean) => {
        setIsOpen(open);
        if (open) {
            setTimeout(() => calculatePosition(), 0);
        } else {
            lastKeyRef.current = '';
            lastKeyIndexRef.current = 0;
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (!isOpen || !/^[a-z0-9]$/i.test(e.key)) return;
        const key = e.key.toLowerCase();
        const matches = options.filter(o => o.label.toLowerCase().startsWith(key));
        if (!matches.length) return;
        if (lastKeyRef.current === key) {
            lastKeyIndexRef.current = (lastKeyIndexRef.current + 1) % matches.length;
        } else {
            lastKeyRef.current = key;
            lastKeyIndexRef.current = 0;
        }
        const match = matches[lastKeyIndexRef.current];
        if (!match) return;
        const targetIndex = options.findIndex(o => o.value === match.value);
        const items = wrapperRef.current?.querySelectorAll('.filter-dropdown-item');
        (items?.[targetIndex] as HTMLElement)?.focus();
        e.preventDefault();
    };

    useEffect(() => {
        const handleResize = () => {
            calculatePosition();
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return (
        <div ref={wrapperRef} className={`filter-dropdown-wrapper ${disabled ? 'disabled' : ''}`} onKeyDown={handleKeyDown}>
            <Dropdown
                onSelect={handleSelect}
                onToggle={handleToggle}
                className="filter-dropdown"
                id="filter-dropdown"
            >
                <Dropdown.Toggle
                    disabled={disabled}
                    className="filter-dropdown-toggle"
                    as="button"
                >
                    <span className="filter-dropdown-text">
                        {labelPart && !selectedOption ? (
                            // Show label + value only when nothing is selected (placeholder state)
                            <>
                                <span className="filter-label">{labelPart}:</span>{' '}
                                <span className="filter-value">{valuePart}</span>
                            </>
                        ) : selectedOption && renderSelectedValue ? (
                            // Show custom rendered value when something is selected and custom renderer provided
                            <span className="filter-value">{renderSelectedValue(selectedOption)}</span>
                        ) : selectedOption ? (
                            // Show only value when something is selected
                            <span className="filter-value">{valuePart}</span>
                        ) : (
                            // Fallback
                            displayText
                        )}
                    </span>
                    {!isDefaultValue ? (
                        <span
                            className="filter-dropdown-icon-wrapper"
                            onClick={handleClear}
                        >
                            <Icon name="close-search" className="filter-dropdown-icon" />
                        </span>
                    ) : (
                        <Icon name="feather-chevron-down" className="filter-dropdown-icon" />
                    )}
                </Dropdown.Toggle>

                <Dropdown.Menu className={`filter-dropdown-menu ${positionClass}`}>
                    {options.map(option => (
                        <Dropdown.Item
                            key={option.value}
                            eventKey={option.value}
                            active={option.value === value}
                            className="filter-dropdown-item"
                        >
                            <span className="filter-option-text">
                                {renderOption ? renderOption(option) : option.label}
                            </span>
                            {option.value === value && (
                                <Icon name="check" className="filter-check-icon" />
                            )}
                        </Dropdown.Item>
                    ))}
                </Dropdown.Menu>
            </Dropdown>
        </div>
    );
};

export default FilterDropdown;

